import { useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { api, jsonBody } from "../../api/client";
import type { ListResponse } from "../../api/types";
import { useSensitiveAction } from "../../auth/SensitiveActionGate";
import { formatDate, titleCase } from "../../lib/format";
import {
  Card,
  CardHeader,
  DataTable,
  EmptyState,
  ErrorPanel,
  Field,
  FormActions,
  Notice,
  StatusPill,
} from "../../ui/components";
import { PanelSkeleton } from "../../ui/skeletons";
import type { CustomDomain, DomainInstructions, WorkspaceSubdomain } from "./types";

// Two kinds of address, kept apart because the answer to "who fixes this?" is different. The
// workspace address is ours: we created it, we publish it, and a failure here is our problem. A
// custom domain belongs to the customer, who proves they own it and controls its DNS.
export function AddressesPanel() {
  const sensitive = useSensitiveAction({
    setupTitle: "Protect web-address changes with two-step verification",
    lockedTitle: "Confirm your identity to manage web addresses",
    lockedDescription:
      "These changes affect how customers reach your pages. Enter a fresh code before continuing.",
  });
  const subdomain = useQuery({
    queryKey: ["integrations", "subdomain"],
    queryFn: () => api<WorkspaceSubdomain>("/api/v1/integrations/subdomain"),
    // A request that is still with the registrar resolves on its own, so the screen follows it
    // rather than leaving somebody pressing refresh to find out what happened.
    refetchInterval: (query) => (query.state.data?.status === "pending" ? 3_000 : false),
  });
  const domains = useQuery({
    queryKey: ["domains"],
    queryFn: () => api<ListResponse<CustomDomain>>("/api/v1/domains"),
    refetchInterval: 30_000,
  });
  const queryClient = useQueryClient();
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<unknown>();
  const [instructions, setInstructions] = useState<DomainInstructions | null>(null);
  const [releasing, setReleasing] = useState<CustomDomain>();

  async function requestSubdomain() {
    setWorking(true);
    setError(undefined);
    try {
      // The reply is kept rather than thrown away in favor of asking the question again. The
      // row is written by the job, so for a moment after the request is accepted there is no
      // row to read, and asking in that moment is answered "not requested": the same answer
      // this screen is given about a workspace that never asked for anything. It would then
      // stop following a request that is genuinely in flight and settle on "no address yet"
      // until somebody reloaded, which is the refresh this panel promises nobody presses.
      // The reply to the request is the one answer that knows the request was made.
      const requested = await api<WorkspaceSubdomain>("/api/v1/integrations/subdomain/retry", {
        method: "POST",
        body: jsonBody({}),
      });
      queryClient.setQueryData(["integrations", "subdomain"], requested);
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  async function createDomain(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    setWorking(true);
    setError(undefined);
    setInstructions(null);
    try {
      setInstructions(
        await api<DomainInstructions>("/api/v1/domains", {
          method: "POST",
          body: jsonBody({
            hostname: String(form.get("hostname")),
            purpose: String(form.get("purpose")),
          }),
        }),
      );
      formElement.reset();
      await domains.refetch();
    } catch (caught) {
      if (!sensitive.markProtectedError(caught)) setError(caught);
    } finally {
      setWorking(false);
    }
  }

  async function reissue(item: CustomDomain) {
    setWorking(true);
    setError(undefined);
    try {
      setInstructions(
        await api<DomainInstructions>(`/api/v1/domains/${item.id}/verification`, {
          method: "POST",
          body: jsonBody({}),
        }),
      );
      await domains.refetch();
    } catch (caught) {
      if (!sensitive.markProtectedError(caught)) setError(caught);
    } finally {
      setWorking(false);
    }
  }

  async function verify(item: CustomDomain) {
    setWorking(true);
    setError(undefined);
    try {
      await api(`/api/v1/domains/${item.id}/verify`, { method: "POST", body: jsonBody({}) });
      await domains.refetch();
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  async function release(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!releasing) return;
    const reason = String(new FormData(event.currentTarget).get("reason") ?? "");
    setWorking(true);
    setError(undefined);
    try {
      await api<void>(`/api/v1/domains/${releasing.id}`, {
        method: "DELETE",
        body: jsonBody({ reason }),
      });
      setReleasing(undefined);
      setInstructions(null);
      await domains.refetch();
    } catch (caught) {
      if (!sensitive.markProtectedError(caught)) setError(caught);
    } finally {
      setWorking(false);
    }
  }

  if (subdomain.isLoading || domains.isLoading) return <PanelSkeleton />;
  const workspace = subdomain.data;
  const published = workspace?.status === "ready";
  const inFlight = workspace?.status === "pending";
  return (
    <div className="stack">
      <Notice tone="info" title="Protected controls">
        Adding a domain, rotating a verification token, and releasing a domain require a recent
        two-step verification. Checking DNS does not.
      </Notice>
      {sensitive.view}
      {error || subdomain.error || domains.error ? (
        <ErrorPanel error={error ?? subdomain.error ?? domains.error} />
      ) : null}
      <Card>
        <CardHeader
          title="Workspace address"
          description="The address we publish for you inside our own domain. It works without any DNS setup on your side."
          actions={workspace ? <StatusPill value={workspace.status} /> : null}
        />
        <div className="stack">
          {published ? (
            <dl className="definition-list">
              <dt>Address</dt>
              <dd className="mono">{workspace.hostname}</dd>
              <dt>Published by</dt>
              <dd>{titleCase(workspace.provider)}</dd>
              <dt>Ready since</dt>
              <dd>{formatDate(workspace.readyAt)}</dd>
            </dl>
          ) : null}
          {inFlight ? (
            <Notice tone="info">
              The address has been requested and is being published. This page updates itself.
            </Notice>
          ) : null}
          {workspace?.status === "not_requested" ? (
            <EmptyState
              title="No workspace address yet"
              description="Ask for one and we will publish it in our domain, including the records that let email reach you."
            />
          ) : null}
          {workspace?.lastError ? (
            <Notice tone="danger" title="The address could not be published">
              {workspace.lastError}
            </Notice>
          ) : null}
          {published && workspace.records.length ? (
            <DataTable
              caption="Published records"
              items={workspace.records}
              getKey={(item) => `${item.type} ${item.name}`}
              columns={[
                { label: "Type", render: (item) => item.type },
                { label: "Name", render: (item) => <span className="mono">{item.name}</span> },
                { label: "Points to", render: (item) => <span className="mono">{item.data}</span> },
                { label: "Priority", render: (item) => item.priority ?? "Not applicable" },
              ]}
            />
          ) : null}
          <div className="inline">
            <button
              className="button primary"
              type="button"
              disabled={working || published || inFlight}
              onClick={requestSubdomain}
            >
              {workspace?.status === "failed" ? "Try again" : "Request address"}
            </button>
          </div>
          {published ? (
            <small>
              Already published. Nothing here needs to change unless the address itself stops
              resolving.
            </small>
          ) : null}
        </div>
      </Card>
      <Card>
        <CardHeader
          title="Custom domains"
          description="Use a hostname you own for public booking or the customer portal. Staff administration stays on the main application address."
        />
        <form className="form-grid" onSubmit={createDomain}>
          <Field
            label="Hostname"
            hint="Use a dedicated subdomain, for example bookings.example.gov."
          >
            <input name="hostname" required placeholder="bookings.example.gov" />
          </Field>
          <Field label="Surface">
            <select name="purpose" defaultValue="public_booking">
              <option value="public_booking">Public booking</option>
              <option value="customer_portal">Customer portal</option>
            </select>
          </Field>
          <div className="full">
            <FormActions
              submitting={working}
              disabled={!sensitive.unlocked}
              submitLabel="Add domain"
            />
          </div>
        </form>
        {instructions ? (
          <Notice tone="warning" title="DNS records: token shown only now">
            <span>
              Add TXT <strong className="mono">{instructions.txtName}</strong> with value{" "}
              <strong className="mono">{instructions.txtValue}</strong>, and CNAME{" "}
              <strong className="mono">{instructions.cnameName}</strong> to{" "}
              <strong className="mono">{instructions.cnameValue}</strong>. Then use Check DNS.
            </span>
          </Notice>
        ) : null}
        {domains.data?.items.length ? (
          <DataTable
            caption="Custom domains"
            items={domains.data.items}
            getKey={(item) => item.id}
            columns={[
              {
                label: "Hostname",
                render: (item) => (
                  <>
                    <strong>{item.hostname}</strong>
                    <br />
                    <small>{titleCase(item.purpose)}</small>
                  </>
                ),
              },
              { label: "Domain", render: (item) => <StatusPill value={item.status} /> },
              {
                label: "Certificate",
                render: (item) => <StatusPill value={item.certificateStatus} />,
              },
              {
                label: "Health",
                render: (item) => (
                  <span>
                    <strong>
                      {item.consecutiveFailures
                        ? `${item.consecutiveFailures} failed checks`
                        : "Healthy"}
                    </strong>
                    <br />
                    <small>
                      {item.certificateExpiresAt
                        ? `Certificate expires ${formatDate(item.certificateExpiresAt)}`
                        : item.nextCheckAt
                          ? `Next check ${formatDate(item.nextCheckAt)}`
                          : `Last checked ${formatDate(item.lastCheckedAt)}`}
                    </small>
                    {item.failureReason ? (
                      <>
                        <br />
                        <small>{item.failureReason}</small>
                      </>
                    ) : null}
                  </span>
                ),
              },
              {
                label: "Target",
                render: (item) => <span className="mono">{item.targetHostname}</span>,
              },
              {
                label: "Action",
                render: (item) =>
                  item.releasedAt ? (
                    "Released"
                  ) : (
                    <div className="inline">
                      <button
                        className="button secondary small"
                        type="button"
                        disabled={working}
                        onClick={() => verify(item)}
                      >
                        Check DNS
                      </button>
                      <button
                        className="button secondary small"
                        type="button"
                        disabled={working || !sensitive.unlocked}
                        onClick={() => reissue(item)}
                      >
                        Rotate token
                      </button>
                      <button
                        className="button danger small"
                        type="button"
                        disabled={working || !sensitive.unlocked}
                        onClick={() => setReleasing(item)}
                      >
                        Release
                      </button>
                    </div>
                  ),
              },
            ]}
          />
        ) : (
          <EmptyState
            title="No custom domains"
            description="The workspace address keeps working until a hostname you own is added and verified."
          />
        )}
      </Card>
      {releasing ? (
        <Card>
          <CardHeader
            title={`Release ${releasing.hostname}`}
            description="This removes the public route immediately and quarantines the hostname before reuse. Existing records remain auditable."
          />
          <form className="form-stack" onSubmit={release}>
            <Field label="Reason">
              <textarea
                name="reason"
                required
                minLength={10}
                defaultValue="Domain is no longer used by this tenant."
              />
            </Field>
            <div className="inline">
              <button
                className="button danger"
                type="submit"
                disabled={working || !sensitive.unlocked}
              >
                Release domain
              </button>
              <button
                className="button ghost"
                type="button"
                onClick={() => setReleasing(undefined)}
              >
                Cancel
              </button>
            </div>
          </form>
        </Card>
      ) : null}
    </div>
  );
}
